import React from 'react';
import { Download, FileArchive, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const DownloadPage = () => {
  const handleDownload = () => {
    window.location.href = '/aionwise-hostinger-deployment.zip';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-6">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-full p-6">
              <FileArchive className="w-16 h-16 text-orange-400" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            Download Your Website
          </h1>
          <p className="text-xl text-gray-400">
            Complete AionWise Technologies deployment package ready for Hostinger
          </p>
        </div>

        {/* Main Download Card */}
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                aionwise-hostinger-deployment.zip
              </h2>
              <p className="text-gray-400">Production-ready build • 425 KB</p>
            </div>
            <Button 
              size="lg"
              onClick={handleDownload}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Now
            </Button>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <h3 className="text-white font-semibold mb-4">Package Includes:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Complete Website Files</p>
                  <p className="text-gray-400 text-sm">index.html, CSS, JavaScript, assets</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Server Configuration</p>
                  <p className="text-gray-400 text-sm">.htaccess for proper routing</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Quick Start Guide</p>
                  <p className="text-gray-400 text-sm">3-step deployment instructions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Full Documentation</p>
                  <p className="text-gray-400 text-sm">Detailed deployment manual</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Website Features */}
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-white mb-6">Your Website Includes:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">30 Real Projects Portfolio</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">6 AI Services Showcase</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">Contact Forms</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">WhatsApp Integration</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">Sunrise Orange/Golden Theme</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">Fully Responsive Design</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">Production Optimized</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400">✓</span>
              <span className="text-gray-300">Fast Load Times (~2s)</span>
            </div>
          </div>
        </div>

        {/* Deployment Steps */}
        <div className="bg-gradient-to-r from-orange-600/20 to-amber-600/20 border border-orange-500/30 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-6">Quick Deployment Steps:</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div>
                <p className="text-white font-semibold">Download & Extract</p>
                <p className="text-gray-300 text-sm">Click the download button above and extract the ZIP file</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div>
                <p className="text-white font-semibold">Upload to Hostinger</p>
                <p className="text-gray-300 text-sm">Log in to Hostinger → File Manager → Upload to public_html</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div>
                <p className="text-white font-semibold">Connect Domain & SSL</p>
                <p className="text-gray-300 text-sm">Add your domain and enable free SSL certificate</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-orange-500/30">
            <p className="text-white font-semibold mb-2">📖 Detailed instructions included in the package</p>
            <p className="text-gray-300 text-sm">Open QUICK_START.md for step-by-step guidance</p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <a href="/">
            <Button variant="outline" className="border-orange-500 text-orange-400 hover:bg-orange-500/10">
              <ExternalLink className="w-4 h-4 mr-2" />
              Back to Website
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
